import { create } from 'zustand'

interface PlaybackState {
    isPlaying: boolean
    togglePlayback: () => void
    setPlaying: (playing: boolean) => void
    snapshotMin: number
    setSnapshotMin: (min: number) => void
    availableMinutes: number[]
    setAvailableMinutes: (mins: number[]) => void
    isRenderingFrame: boolean
    setRenderingFrame: (isRendering: boolean) => void
    stepForward: () => void
    stepBackward: () => void
    jumpTime: (minutesDelta: number) => void
    playSpeed: number
    setPlaySpeed: (speed: number) => void
}

let animationFrameId: number | null = null;
let lastTickTime = 0;

export const usePlaybackStore = create<PlaybackState>((set, get) => {
    const loop = (timestamp: number) => {
        const state = get();
        if (!state.isPlaying) return;

        const playSpeed = state.playSpeed || 1; // Default speed
        const interval = Math.max(16, 1000 / playSpeed); // Allow up to 60fps
        const elapsed = timestamp - lastTickTime;
        
        if (elapsed >= interval) {
            lastTickTime = timestamp;
            
            // Advance the time
            const times = state.availableMinutes.length > 0
                ? state.availableMinutes
                : Array.from({ length: 376 }, (_, i) => 555 + i); // 09:15 to 15:30 fallback
                
            let currentIdx = times.findIndex((t) => t >= state.snapshotMin)
            if (currentIdx < 0) currentIdx = 0

            const nextIdx = (currentIdx + 1) % times.length;
            console.log(`[Playback] Tick! Advance from ${state.snapshotMin} to ${times[nextIdx]} (idx ${currentIdx} -> ${nextIdx})`);
            
            // Update state
            set({ snapshotMin: times[nextIdx]! });
        }

        animationFrameId = requestAnimationFrame(loop);
    };

    const startLoop = () => {
        console.log(`[Playback] startLoop called. Current isPlaying: ${get().isPlaying}, animationFrameId: ${animationFrameId}`);
        if (!animationFrameId) {
            lastTickTime = 0; // Reset to force immediate first tick
            animationFrameId = requestAnimationFrame(loop);
            console.log(`[Playback] Started animation frame: ${animationFrameId}`);
        }
    };

    const stopLoop = () => {
        if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
            animationFrameId = null;
        }
    };

    return {
        isPlaying: false,
        togglePlayback: () => {
            const willPlay = !get().isPlaying;
            console.log(`[Playback] togglePlayback clicked. New state will be: ${willPlay}`);
            set({ isPlaying: willPlay, isRenderingFrame: false });
            if (willPlay) startLoop();
            else stopLoop();
        },
        setPlaying: (playing: boolean) => {
            console.log(`[Playback] setPlaying called with: ${playing}`);
            set({ isPlaying: playing, isRenderingFrame: false });
            if (playing) startLoop();
            else stopLoop();
        },
        snapshotMin: 930, // 15:30 default
        setSnapshotMin: (min: number) => set({ snapshotMin: min }),
        availableMinutes: [],
        setAvailableMinutes: (mins: number[]) => set({ availableMinutes: mins }),
        isRenderingFrame: false,
        setRenderingFrame: (isRendering: boolean) => set({ isRenderingFrame: isRendering }),
        stepForward: () => {
            const state = get();
            const times = state.availableMinutes.length > 0 ? state.availableMinutes : [];
            if (times.length === 0) return;
            const idx = times.findIndex((t) => t > state.snapshotMin);
            set({ snapshotMin: idx >= 0 ? times[idx]! : times[0]! });
        },
        stepBackward: () => {
            const state = get();
            const times = state.availableMinutes.length > 0 ? state.availableMinutes : [];
            if (times.length === 0) return;
            const reversed = [...times].reverse();
            const idx = reversed.findIndex((t) => t < state.snapshotMin);
            set({ snapshotMin: idx >= 0 ? reversed[idx]! : reversed[0]! });
        },
        jumpTime: (minutesDelta: number) => {
            const state = get();
            const times = state.availableMinutes.length > 0 ? state.availableMinutes : [];
            if (times.length === 0) return;
            
            const targetMin = state.snapshotMin + minutesDelta;
            
            // Find the closest available snapshot time
            let closest = times[0]!;
            let minDiff = Math.abs(times[0]! - targetMin);
            
            for (let i = 1; i < times.length; i++) {
                const diff = Math.abs(times[i]! - targetMin);
                if (diff < minDiff) {
                    minDiff = diff;
                    closest = times[i]!;
                }
            }
            
            // If the jump resulted in no movement (e.g. data is sparse),
            // force at least one step in the desired direction
            if (closest === state.snapshotMin) {
                if (minutesDelta > 0) {
                    const idx = times.findIndex((t) => t > state.snapshotMin);
                    if (idx >= 0) closest = times[idx]!;
                } else if (minutesDelta < 0) {
                    const reversed = [...times].reverse();
                    const idx = reversed.findIndex((t) => t < state.snapshotMin);
                    if (idx >= 0) closest = reversed[idx]!;
                }
            }
            
            set({ snapshotMin: closest });
        },
        playSpeed: 1,
        setPlaySpeed: (speed: number) => set({ playSpeed: speed })
    };
})
